import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/base/Modal";
import { StatusBadge } from "@/components/base/StatusBadge";
import AttributesTab from "./AttributesTab";
import IdentifiersTab from "./IdentifiersTab";
import { inputCls, labelCls, type ExternalIdRow, type ProductRow, type VariantRow } from "../types";

// "Product Data" feature: manufacturing/customs/compliance fields, plus the
// Attributes, Identifiers, and External Identifier sub-modules. Owns its
// external-identifier mutations.

interface Props {
  productId: string;
  orgId: string;
  product: ProductRow;
  variants: VariantRow[];
  externalIds: ExternalIdRow[];
  connectors: { id: string; name: string }[];
  onEdit: () => void;
}

export default function AdvancedTab({ productId, orgId, product, variants, externalIds, connectors, onEdit }: Props) {
  const queryClient = useQueryClient();
  const [extOpen, setExtOpen] = useState(false);
  const [extSaving, setExtSaving] = useState(false);
  const [extError, setExtError] = useState("");
  const [extDeleting, setExtDeleting] = useState<string | null>(null);
  const [extForm, setExtForm] = useState({ connector_id: "", external_type: "", external_id: "" });

  const addExternalId = async (e: FormEvent) => {
    e.preventDefault();
    setExtError("");
    if (!extForm.external_type.trim() || !extForm.external_id.trim()) {
      setExtError("External type and ID are required.");
      return;
    }
    setExtSaving(true);
    const { error: insErr } = await supabase.from("external_identifiers").insert({
      org_id: orgId,
      entity_type: "product",
      entity_id: productId,
      connector_id: extForm.connector_id || null,
      external_type: extForm.external_type.trim(),
      external_id: extForm.external_id.trim(),
      sync_status: "pending",
    });
    setExtSaving(false);
    if (insErr) {
      setExtError(insErr.message);
      return;
    }
    setExtOpen(false);
    setExtForm({ connector_id: "", external_type: "", external_id: "" });
    queryClient.invalidateQueries({ queryKey: ["external-ids", orgId, productId] });
  };

  const removeExternalId = async (extId: string) => {
    setExtDeleting(extId);
    await supabase.from("external_identifiers").delete().eq("id", extId);
    setExtDeleting(null);
    queryClient.invalidateQueries({ queryKey: ["external-ids", orgId, productId] });
  };

  const p = product;

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Product Data</h3>
              <p className="text-xs text-slate-400 mt-0.5">Manufacturing, customs and compliance details.</p>
            </div>
            <button onClick={onEdit} className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap">Edit</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div><p className="text-xs text-slate-400">Manufacturer</p><p className="text-sm font-medium text-slate-800">{p.manufacturer || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Model number</p><p className="text-sm font-medium text-slate-800">{p.model_number || "—"}</p></div>
            <div><p className="text-xs text-slate-400">MPN</p><p className="text-sm font-medium text-slate-800 font-mono">{p.mpn || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Country of origin</p><p className="text-sm font-medium text-slate-800">{p.country_of_origin || "—"}</p></div>
            <div><p className="text-xs text-slate-400">HS tariff code</p><p className="text-sm font-medium text-slate-800 font-mono">{p.hs_tariff_code || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Unit of measure</p><p className="text-sm font-medium text-slate-800">{p.unit_of_measure || p.unit || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Condition</p><p className="text-sm font-medium text-slate-800">{p.condition || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Tax profile</p><p className="text-sm font-medium text-slate-800">{p.tax_profile || "—"}</p></div>
            <div><p className="text-xs text-slate-400">Shipping profile</p><p className="text-sm font-medium text-slate-800">{p.shipping_profile || "—"}</p></div>
            <div className="sm:col-span-2"><p className="text-xs text-slate-400">Customs description</p><p className="text-sm font-medium text-slate-800">{p.customs_description || "—"}</p></div>
          </div>
        </div>
        <AttributesTab productId={productId} orgId={orgId} />
        <IdentifiersTab productId={productId} orgId={orgId} variants={variants} />
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">External Identifiers</h3>
              <p className="text-xs text-slate-400 mt-0.5">Map this product to marketplace and channel records</p>
            </div>
            <button
              onClick={() => {
                setExtError("");
                setExtOpen(true);
              }}
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
            >
              <i className="ri-add-line" /> Add mapping
            </button>
          </div>
          {(externalIds ?? []).length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">No external mappings yet</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {(externalIds ?? []).map((x) => {
                const connectorName = (connectors ?? []).find((c) => c.id === x.connector_id)?.name;
                return (
                  <div key={x.id} className="px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800 font-mono">{x.external_id}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {x.external_type}
                        {connectorName ? ` · ${connectorName}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge
                        status={x.sync_status === "synced" ? "success" : x.sync_status === "failed" ? "error" : "pending"}
                        customLabel={x.sync_status}
                      />
                      <button
                        onClick={() => removeExternalId(x.id)}
                        disabled={extDeleting === x.id}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={extOpen}
        onClose={() => setExtOpen(false)}
        title="Add External Identifier"
        footer={
          <>
            <button onClick={() => setExtOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={addExternalId} disabled={extSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {extSaving ? "Adding..." : "Add Mapping"}
            </button>
          </>
        }
      >
        <form onSubmit={addExternalId} className="space-y-4">
          {extError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{extError}</div>}
          <div>
            <label className={labelCls}>Connector</label>
            <select value={extForm.connector_id} onChange={(e) => setExtForm((f) => ({ ...f, connector_id: e.target.value }))} className={inputCls}>
              <option value="">No connector (unassigned)</option>
              {(connectors ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>External type *</label>
            <input required value={extForm.external_type} onChange={(e) => setExtForm((f) => ({ ...f, external_type: e.target.value }))} className={inputCls} placeholder="shopify_variant_id, amazon_asin, channel_sku..." />
          </div>
          <div>
            <label className={labelCls}>External ID *</label>
            <input required value={extForm.external_id} onChange={(e) => setExtForm((f) => ({ ...f, external_id: e.target.value }))} className={inputCls} placeholder="e.g. 4430001234567" />
          </div>
        </form>
      </Modal>
    </>
  );
}